import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  KatanaPathDumpView,
  KatanaPathElementDetail,
  KatanaPathSummary,
} from '@/types';
import { formatCellValue } from './constants';
import { InfiniteScrollObserver } from '../../helpers/InfiniteScrollObserver';

type PathSummaryResultsProps = {
  hasAppliedFilters: boolean;
  isLoading: boolean;
  isFetching: boolean;
  mode: KatanaPathDumpView;
  results: KatanaPathSummary[];
  canLoadMore: boolean;
  onLoadMore: () => void;
  isTranslationLoading: boolean;
  translationErrorMessage: string | null;
  resolveTranslationStatus: (row: KatanaPathSummary) => {
    status: string;
    reasonCode: string | null;
    reasonMessage: string | null;
    deliveredDth: number | null;
    pathFingerprint: string | null;
    pathRecordId: number | null;
    missingPhysicalPointIds: number[];
    missingNodePointIds: number[];
  } | null;
  onRequestPathElements: (row: KatanaPathSummary) => void;
  resolvePathElements: (row: KatanaPathSummary) => KatanaPathElementDetail[];
  isPathElementsLoading: (row: KatanaPathSummary) => boolean;
  resolvePathElementsError: (row: KatanaPathSummary) => string | null;
  onDisplayedSourcePathIdsChange?: (ids: number[]) => void;
};

type SummaryColumnKey = keyof KatanaPathSummary | 'translation_status';

const summaryColumns: Array<{ label: string; key: SummaryColumnKey }> = [
  { label: 'Route', key: 'route_list' },
  { label: 'Translation', key: 'translation_status' },
  { label: 'Receipt Point', key: 'r_point_name' },
  { label: 'Delivery Point', key: 'd_point_name' },
  { label: 'Delivered Volume', key: 'max_volume' },
  { label: 'TSPs', key: 'tsp_list' },
  { label: 'Desks', key: 'desk_names' },
  { label: 'Contracts', key: 'contract_list' },
];

const summaryFallbackColumn: keyof KatanaPathSummary = 'route_list';

const detailSummaryRows: Array<{ label: string; key: keyof KatanaPathSummary }> = [
  { label: 'Receipt Point', key: 'r_point_name' },
  { label: 'Delivery Point', key: 'd_point_name' },
  { label: 'Delivered Volume', key: 'max_volume' },
  { label: 'TSPs', key: 'tsp_list' },
  { label: 'Contracts', key: 'contract_list' },
];

const detailCardStyles = [
  {
    backgroundColor: 'var(--bs-body-bg)',
    borderColor: 'var(--bs-border-color)',
  },
  {
    backgroundColor: 'var(--bs-secondary-bg)',
    borderColor: 'rgba(var(--bs-primary-rgb), 0.35)',
  },
];

const tableHeaderStyle = {
  backgroundColor: 'var(--bs-tertiary-bg)',
  color: 'var(--bs-emphasis-color)',
};

type SummarySortState = {
  key: SummaryColumnKey | null;
  direction: 'asc' | 'desc';
};

const summarySortDefaults: SummarySortState = {
  key: null,
  direction: 'asc',
};

const toTranslationBadgeClass = (status: string): string => {
  const normalized = status.trim().toLowerCase();

  if (!normalized || normalized === 'unknown') {
    return 'text-bg-secondary';
  }

  if (
    normalized.includes('resolved') ||
    normalized.includes('translated') ||
    normalized.includes('success') ||
    normalized.includes('matched') ||
    normalized === 'ok'
  ) {
    return 'text-bg-success';
  }

  if (
    normalized.includes('pending') ||
    normalized.includes('queued') ||
    normalized.includes('running')
  ) {
    return 'text-bg-warning';
  }

  if (
    normalized.includes('missing') ||
    normalized.includes('error') ||
    normalized.includes('failed') ||
    normalized.includes('unresolved')
  ) {
    return 'text-bg-danger';
  }

  return 'text-bg-secondary';
};

const getRouteListDisplayText = (row: KatanaPathSummary): string =>
  formatCellValue(
    row.route_list || row.original_path_key || row.original_path_fingerprint || 'Path',
  );

const normalizeSearchText = (value: unknown): string =>
  formatCellValue(value).toLowerCase().replace(/\s+/g, ' ').trim();

const toNumericId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

export const PathSummaryResults: React.FC<PathSummaryResultsProps> = React.memo(
  ({
    hasAppliedFilters,
    isLoading,
    isFetching,
    mode,
    results,
    canLoadMore,
    onLoadMore,
    isTranslationLoading,
    translationErrorMessage,
    resolveTranslationStatus,
    onRequestPathElements,
    resolvePathElements,
    isPathElementsLoading,
    resolvePathElementsError,
    onDisplayedSourcePathIdsChange,
  }) => {
    const scrollRootRef = useRef<HTMLDivElement | null>(null);
    const [sortState, setSortState] = useState<SummarySortState>(summarySortDefaults);
    const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
    const [columnFilters, setColumnFilters] = useState<
      Partial<Record<SummaryColumnKey, string>>
    >({});

    const toSummaryRowKey = (result: KatanaPathSummary, index: number): string =>
      String(
        result.source_path_id ??
          result.id ??
          result.translation_group_key ??
          result.original_path_key ??
          index,
      );

    const getSummaryComparableValue = (
      row: KatanaPathSummary,
      key: SummaryColumnKey,
    ): number | string => {
      if (key === 'translation_status') {
        return (resolveTranslationStatus(row)?.status ?? 'Unknown').toLowerCase();
      }

      const value = row[key] as unknown;
      if (typeof value === 'number') return value;
      if (typeof value === 'boolean') return value ? 1 : 0;
      return formatCellValue(value).toLowerCase();
    };

    const filteredResults = useMemo(() => {
      if (results.length === 0) return [];
      return results.filter((result) =>
        summaryColumns.every((column) => {
          const filterValue = normalizeSearchText(columnFilters[column.key] ?? '');
          if (!filterValue) return true;

          if (column.key === 'translation_status') {
            const status = resolveTranslationStatus(result)?.status ?? 'Unknown';
            return normalizeSearchText(status).includes(filterValue);
          }

          const value =
            column.key === summaryFallbackColumn
              ? getRouteListDisplayText(result)
              : result[column.key];

          return normalizeSearchText(value).includes(filterValue);
        }),
      );
    }, [results, columnFilters, resolveTranslationStatus]);

    const displayedResults = useMemo(() => {
      if (!sortState.key) return filteredResults;

      const sorted = [...filteredResults];
      sorted.sort((resultA, resultB) => {
        const valueA = getSummaryComparableValue(resultA, sortState.key as SummaryColumnKey);
        const valueB = getSummaryComparableValue(resultB, sortState.key as SummaryColumnKey);

        if (typeof valueA === 'number' && typeof valueB === 'number') {
          const diff = valueA - valueB;
          return sortState.direction === 'asc' ? diff : -diff;
        }

        const diff = String(valueA).localeCompare(String(valueB));
        return sortState.direction === 'asc' ? diff : -diff;
      });

      return sorted;
    }, [filteredResults, sortState, resolveTranslationStatus]);

    useEffect(() => {
      if (!onDisplayedSourcePathIdsChange) return;

      const ids = new Set<number>();
      displayedResults.forEach((row) => {
        const sourcePathId = toNumericId(row.source_path_id) ?? toNumericId(row.id);

        if (typeof sourcePathId === 'number') {
          ids.add(sourcePathId);
        }
      });

      onDisplayedSourcePathIdsChange(Array.from(ids));
    }, [displayedResults, onDisplayedSourcePathIdsChange]);

    const hasColumnFilters = Object.values(columnFilters).some((value) =>
      Boolean(value?.trim()),
    );

    const updateSort = (key: SummaryColumnKey) => {
      setSortState((prev) => {
        if (prev.key !== key) {
          return { key, direction: 'asc' };
        }

        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      });
    };

    const bottomStatusText = (() => {
      if (!hasAppliedFilters || isLoading || results.length === 0) return null;
      if (isFetching) return 'Loading more paths...';
      if (canLoadMore) return 'Scroll to the bottom to load more paths.';
      return 'All available paths are loaded.';
    })();

    if (!hasAppliedFilters) {
      return (
        <div className="text-center text-muted py-4">
          Configure filters and click Get Paths to load data.
        </div>
      );
    }

    if (isLoading) {
      return <div className="text-center text-muted py-4">Loading...</div>;
    }

    if (results.length === 0) {
      return (
        <div className="text-center text-muted py-4">
          No paths found for the current filter set.
        </div>
      );
    }

    if (mode === 'summary') {
      return (
        <div>
          <div className="small mb-2">
            {isTranslationLoading ? (
              <span className="text-muted">Translation statuses are loading...</span>
            ) : translationErrorMessage ? (
              <span className="text-danger">
                Translation status unavailable: {translationErrorMessage}
              </span>
            ) : (
              <span className="text-muted">Translation statuses are loaded.</span>
            )}
          </div>
          <div ref={scrollRootRef} style={{ overflow: 'auto', maxHeight: '65vh' }}>
            <table className="table table-sm table-striped table-bordered mb-0">
              <thead
                style={{
                  ...tableHeaderStyle,
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                }}
              >
                <tr>
                  <th className="text-start" style={{ minWidth: 90 }}>
                    Details
                  </th>
                  {summaryColumns.map((column) => {
                    const isActiveSort = sortState.key === column.key;
                    const sortIndicator = isActiveSort
                      ? sortState.direction === 'asc'
                        ? ' ▲'
                        : ' ▼'
                      : '';
                    return (
                      <th key={column.key}>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none"
                          onClick={() => updateSort(column.key)}
                          title={`Sort by ${column.label}`}
                        >
                          {column.label}
                          {sortIndicator}
                        </button>
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  <th />
                  {summaryColumns.map((column) => (
                    <th key={`${column.key}-filter`}>
                      <input
                        className="form-control form-control-sm"
                        type="text"
                        autoComplete="off"
                        name={`summary-filter-${String(column.key)}`}
                        value={columnFilters[column.key] ?? ''}
                        onChange={(evt) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            [column.key]: evt.target.value,
                          }))
                        }
                        placeholder={`Filter ${column.label}`}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedResults.length === 0 && (
                  <tr>
                    <td
                      colSpan={summaryColumns.length + 1}
                      className="text-center text-muted py-3"
                    >
                      {hasColumnFilters
                        ? 'No paths on this page match the column filters.'
                        : 'No paths to display.'}
                    </td>
                  </tr>
                )}
                {displayedResults.map((result, index) => {
                  const rowKey = toSummaryRowKey(result, index);
                  const isExpanded = expandedRowKey === rowKey;
                  const pathElements = resolvePathElements(result);
                  const isPathElementsPending = isPathElementsLoading(result);
                  const pathElementsError = resolvePathElementsError(result);
                  const translationStatus = resolveTranslationStatus(result);

                  return (
                    <React.Fragment key={rowKey}>
                      <tr>
                        <td className="text-start">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedRowKey(null);
                                return;
                              }

                              setExpandedRowKey(rowKey);
                              onRequestPathElements(result);
                            }}
                            title={isExpanded ? 'Hide details' : 'Show details'}
                            aria-label={isExpanded ? 'Hide details' : 'Show details'}
                          >
                            {isExpanded ? '-' : '+'}
                          </button>
                        </td>
                        {summaryColumns.map((column) => {
                          if (column.key === 'translation_status') {
                            const status = translationStatus?.status ?? 'Unknown';
                            return (
                              <td key={column.key}>
                                <span
                                  className={`badge rounded-pill ${toTranslationBadgeClass(status)}`}
                                >
                                  {status}
                                </span>
                              </td>
                            );
                          }

                          if (column.key === summaryFallbackColumn) {
                            return (
                              <td key={column.key}>
                                {getRouteListDisplayText(result)}
                              </td>
                            );
                          }

                          return (
                            <td key={column.key}>{formatCellValue(result[column.key])}</td>
                          );
                        })}
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan={summaryColumns.length + 1} className="p-0">
                            <div className="border-top px-3 py-3">
                              <div className="row g-3">
                                <div className="col-12 col-xl-8">
                                  <div className="fw-semibold mb-2">Path Elements</div>
                                  {isPathElementsPending ? (
                                    <div className="small text-muted">
                                      Loading path elements...
                                    </div>
                                  ) : pathElementsError ? (
                                    <div className="small text-danger">{pathElementsError}</div>
                                  ) : pathElements.length === 0 ? (
                                    <div className="small text-muted">
                                      No path elements returned for this path.
                                    </div>
                                  ) : (
                                    <table className="table table-sm table-striped table-hover table-bordered mb-0">
                                      <thead style={tableHeaderStyle}>
                                        <tr>
                                          <th>Link Order</th>
                                          <th>Nom R Point</th>
                                          <th>Nom D Point</th>
                                          <th>Nom TSP</th>
                                          <th>Link R Vol</th>
                                          <th>Link D Vol</th>
                                          <th>Transport Trade ID</th>
                                          <th>Receipt Trade Type</th>
                                          <th>Delivery Trade Type</th>
                                          <th>Nom R Vol</th>
                                          <th>Nom D Vol</th>
                                          <th>Nom Fuel</th>
                                          <th>Receipt Interconnect</th>
                                          <th>Delivery Interconnect</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {pathElements.map((element, elementIndex) => (
                                          <tr
                                            key={`${rowKey}-${element.link_order ?? 'na'}-${elementIndex}`}
                                          >
                                            <td>{formatCellValue(element.link_order)}</td>
                                            <td>{formatCellValue(element.nom_r_point_name)}</td>
                                            <td>{formatCellValue(element.nom_d_point_name)}</td>
                                            <td>{formatCellValue(element.nom_tsp_name)}</td>
                                            <td>
                                              {formatCellValue(element.link_receipt_volume)}
                                            </td>
                                            <td>
                                              {formatCellValue(element.link_delivery_volume)}
                                            </td>
                                            <td>
                                              {formatCellValue(element.transport_trade_id)}
                                            </td>
                                            <td>
                                              {formatCellValue(element.receipt_trade_type)}
                                            </td>
                                            <td>
                                              {formatCellValue(element.delivery_trade_type)}
                                            </td>
                                            <td>
                                              {formatCellValue(element.nom_receipt_volume)}
                                            </td>
                                            <td>
                                              {formatCellValue(element.nom_delivery_volume)}
                                            </td>
                                            <td>{formatCellValue(element.nom_fuel_loss)}</td>
                                            <td>
                                              {formatCellValue(element.receipt_interconnect_name)}
                                            </td>
                                            <td>
                                              {formatCellValue(element.delivery_interconnect_name)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </div>

                                <div className="col-12 col-xl-4">
                                  <div className="fw-semibold mb-2">Translation Details</div>
                                  <div className="small d-flex flex-column gap-2">
                                    <div>
                                      <span className="text-muted me-1">Status:</span>
                                      <span
                                        className={`badge rounded-pill ${toTranslationBadgeClass(
                                          translationStatus?.status ?? 'Unknown',
                                        )}`}
                                      >
                                        {translationStatus?.status ?? 'Unknown'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-muted">Delivered Dth:</span>{' '}
                                      {formatCellValue(translationStatus?.deliveredDth ?? null)}
                                    </div>
                                    <div>
                                      <span className="text-muted">Path Fingerprint:</span>{' '}
                                      {formatCellValue(
                                        translationStatus?.pathFingerprint ??
                                          result.original_path_fingerprint ??
                                          null,
                                      )}
                                    </div>
                                    <div>
                                      <span className="text-muted">Path Record ID:</span>{' '}
                                      {formatCellValue(translationStatus?.pathRecordId ?? null)}
                                    </div>
                                    <div>
                                      <span className="text-muted">Reason Code:</span>{' '}
                                      {formatCellValue(translationStatus?.reasonCode ?? null)}
                                    </div>
                                    <div>
                                      <span className="text-muted">Reason Message:</span>{' '}
                                      {formatCellValue(translationStatus?.reasonMessage ?? null)}
                                    </div>
                                    <div>
                                      <span className="text-muted">
                                        Missing Physical Point IDs:
                                      </span>{' '}
                                      {translationStatus?.missingPhysicalPointIds?.length
                                        ? translationStatus.missingPhysicalPointIds.join(', ')
                                        : 'None'}
                                    </div>
                                    <div>
                                      <span className="text-muted">Missing Node Point IDs:</span>{' '}
                                      {translationStatus?.missingNodePointIds?.length
                                        ? translationStatus.missingNodePointIds.join(', ')
                                        : 'None'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {canLoadMore && (
              <InfiniteScrollObserver
                height={24}
                options={{
                  root: scrollRootRef.current,
                  rootMargin: '0px 0px 120px 0px',
                }}
                onScrollIn={() => {
                  if (!isFetching) {
                    onLoadMore();
                  }
                }}
              />
            )}

            {bottomStatusText && (
              <div className="small text-muted text-center py-2">{bottomStatusText}</div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        ref={scrollRootRef}
        className="d-flex flex-column gap-2"
        style={{ overflow: 'auto', maxHeight: '65vh' }}
      >
        {displayedResults.map((result, index) => {
          const pathElements = Array.isArray(result.path_elements) ? result.path_elements : [];
          const cardKey = String(
            result.original_path_key ?? result.original_path_fingerprint ?? index,
          );

          return (
            <div
              key={cardKey}
              className="border rounded p-3"
              style={{
                ...detailCardStyles[index % 2],
                borderWidth: 2,
              }}
            >
              <div className="fw-semibold mb-2">
                {result.route_list || `Path ${index + 1}`}
              </div>

              <div style={{ overflow: 'auto' }}>
                <table className="table table-sm table-striped table-hover table-bordered mb-3">
                  <thead style={tableHeaderStyle}>
                    <tr>
                      {detailSummaryRows.map((item) => (
                        <th key={item.key}>{item.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {detailSummaryRows.map((item) => (
                        <td key={item.key}>{formatCellValue(result[item.key])}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="fw-semibold mb-1">Path Elements</div>
              {pathElements.length === 0 ? (
                <div className="small text-muted">No path elements returned.</div>
              ) : (
                <div style={{ overflow: 'auto' }}>
                  <table className="table table-sm table-striped table-hover table-bordered mb-0">
                    <thead style={tableHeaderStyle}>
                      <tr>
                        <th>Nom R Point</th>
                        <th>Nom D Point</th>
                        <th>Nom TSP</th>
                        <th>Link R Vol</th>
                        <th>Link D Vol</th>
                        <th>Transport Trade ID</th>
                        <th>Receipt Trade Type</th>
                        <th>Delivery Trade Type</th>
                        <th>Nom R Vol</th>
                        <th>Nom D Vol</th>
                        <th>Nom Fuel</th>
                        <th>Receipt Interconnect</th>
                        <th>Delivery Interconnect</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pathElements.map((element, elementIndex) => (
                        <tr
                          key={`${cardKey}-${element.link_order ?? 'na'}-${elementIndex}`}
                        >
                          <td>{formatCellValue(element.nom_r_point_name)}</td>
                          <td>{formatCellValue(element.nom_d_point_name)}</td>
                          <td>{formatCellValue(element.nom_tsp_name)}</td>
                          <td>{formatCellValue(element.link_receipt_volume)}</td>
                          <td>{formatCellValue(element.link_delivery_volume)}</td>
                          <td>{formatCellValue(element.transport_trade_id)}</td>
                          <td>{formatCellValue(element.receipt_trade_type)}</td>
                          <td>{formatCellValue(element.delivery_trade_type)}</td>
                          <td>{formatCellValue(element.nom_receipt_volume)}</td>
                          <td>{formatCellValue(element.nom_delivery_volume)}</td>
                          <td>{formatCellValue(element.nom_fuel_loss)}</td>
                          <td>{formatCellValue(element.receipt_interconnect_name)}</td>
                          <td>{formatCellValue(element.delivery_interconnect_name)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {displayedResults.length === 0 && (
          <div className="text-center text-muted py-4">
            {hasColumnFilters
              ? 'No paths on this page match the column filters.'
              : 'No paths to display.'}
          </div>
        )}

        {canLoadMore && (
          <InfiniteScrollObserver
            height={24}
            options={{
              root: scrollRootRef.current,
              rootMargin: '0px 0px 120px 0px',
            }}
            onScrollIn={() => {
              if (!isFetching) {
                onLoadMore();
              }
            }}
          />
        )}

        {bottomStatusText && (
          <div className="small text-muted text-center py-2">{bottomStatusText}</div>
        )}
      </div>
    );
  },
);

PathSummaryResults.displayName = 'PathSummaryResults';
